<template>
  <el-drawer
      :append-to-body="true"
      :before-close="close"
      :visible="value"
      :with-header="false"
      class="guide-center"
      custom-class="guide-center__drawer"
      direction="rtl"
      size="min(640px, 94vw)"
  >
    <div class="guide-center__shell">
      <header class="guide-center__header">
        <div>
          <span>产品内帮助</span>
          <h2>你现在想完成什么？</h2>
          <p>每次只讲一个真实任务，指到当前页面真正需要操作的位置。</p>
        </div>
        <el-button aria-label="关闭使用指引" circle icon="el-icon-close" @click="close"/>
      </header>

      <el-tabs v-model="activeTab" class="guide-center__tabs" stretch>
        <el-tab-pane label="我现在要做" name="page">
          <div v-if="currentScenarios.length" class="guide-center__content">
            <div class="guide-center__page-intro">
              <el-tag effect="plain" size="mini">{{ currentPrimary.moduleTitle }}</el-tag>
              <div>
                <strong>{{ currentPrimary.pageTitle }}</strong>
                <span>{{ moduleDescription }}</span>
              </div>
            </div>

            <article v-for="item in currentScenarios" :key="item.key" class="guide-task-card">
              <div class="guide-task-card__head">
                <h3>{{ item.intent }}</h3>
                <el-tag :type="statusTag(item).type" effect="plain" size="mini">{{ statusTag(item).label }}</el-tag>
              </div>
              <p class="guide-task-card__answer">看完你会知道：{{ item.understanding }}</p>
              <div class="guide-task-card__context">
                <p><i class="el-icon-user"></i><span><strong>适合谁：</strong>{{ joinText(item.audience) }}</span></p>
                <p><i class="el-icon-circle-check"></i><span><strong>适合现在做：</strong>{{ joinText(item.prerequisites) }}</span></p>
                <p v-if="item.upstream.length"><i class="el-icon-back"></i><span><strong>通常来自：</strong>{{ joinText(item.upstream) }}</span></p>
                <p><i class="el-icon-right"></i><span><strong>接下来：</strong>{{ item.outcome }}</span></p>
                <p v-if="item.downstream.length"><i class="el-icon-connection"></i><span><strong>会衔接：</strong>{{ joinText(item.downstream) }}</span></p>
              </div>
              <div class="guide-task-card__steps">
                <strong>这项任务怎么做</strong>
                <ol>
                  <li v-for="(step, index) in item.steps" :key="`${item.key}-${index}`">
                    <span>{{ index + 1 }}</span>
                    <div>
                      <div class="guide-task-card__step-head">
                        <b>{{ step.title }}</b>
                        <small :class="stepAvailable(step) ? 'is-available' : 'is-unavailable'">
                          {{ stepAvailable(step) ? '当前可定位' : '当前状态不满足' }}
                        </small>
                      </div>
                      <p>{{ step.description }}</p>
                      <em v-if="!stepAvailable(step) && step.unavailable">
                        {{ step.unavailable.title }}：{{ step.unavailable.description }}
                      </em>
                    </div>
                  </li>
                </ol>
              </div>
              <div class="guide-task-card__warning">
                <i class="el-icon-warning-outline"></i>
                <span>{{ item.risk }}</span>
              </div>
              <div class="guide-task-card__footer">
                <span>只高亮说明，不会替你提交业务。</span>
                <el-button icon="el-icon-position" type="primary" @click="replay(item)">
                  {{ getStatus(item) ? '重新指给我看' : '现在指给我看' }}
                </el-button>
              </div>
            </article>
          </div>
          <el-empty v-else description="当前页面不是已开放业务页，暂不提供操作指引"/>
        </el-tab-pane>

        <el-tab-pane label="同模块还能做什么" name="module">
          <div v-if="currentPrimary" class="guide-center__content">
            <div class="guide-center__section-title">
              <div>
                <h3>{{ currentPrimary.moduleTitle }}</h3>
                <p>{{ moduleDescription }}</p>
              </div>
            </div>
            <div class="guide-intent-list">
              <article
                  v-for="item in currentModuleScenarios"
                  :key="item.key"
                  :class="{'is-current': item.routeName === routeName}"
              >
                <div>
                  <small>{{ item.pageTitle }}</small>
                  <strong>{{ item.intent }}</strong>
                  <p>{{ item.understanding }}</p>
                </div>
                <el-button
                    :disabled="item.routeName !== routeName"
                    size="mini"
                    type="text"
                    @click="replay(item)"
                >
                  {{ item.routeName === routeName ? '指给我看' : '进入该页后使用' }}
                </el-button>
              </article>
            </div>
          </div>
          <el-empty v-else description="当前页面没有可用的模块指引"/>
        </el-tab-pane>

        <el-tab-pane label="业务怎么串起来" name="workflow">
          <div class="guide-center__content">
            <div class="guide-workflow-list">
              <article v-for="workflow in accessibleWorkflows" :key="workflow.key" class="guide-workflow-card">
                <h3>{{ workflow.title }}</h3>
                <p>{{ workflow.summary }}</p>
                <div class="guide-workflow-card__nodes">
                  <template v-for="(node, index) in workflow.accessibleNodes">
                    <span :key="`${node.path || node.paths.join('|')}-${index}`">{{ node.title }}</span>
                    <i v-if="index < workflow.accessibleNodes.length - 1" :key="`${node.path || node.paths.join('|')}-${index}-arrow`" class="el-icon-right"></i>
                  </template>
                </div>
                <small>只展示当前账号能访问的节点，这里不会自动跳转。</small>
              </article>
              <el-empty v-if="!accessibleWorkflows.length" description="当前账号暂无可访问的业务链路节点"/>
            </div>

            <section class="guide-login-reference">
              <h3>换新人时，先把这两件事发给他</h3>
              <article v-for="item in referenceScenarios" :key="item.key">
                <strong>{{ item.intent }}</strong>
                <p>{{ item.understanding }}</p>
                <small>{{ item.risk }}</small>
              </article>
            </section>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>

<script>
import {
  filterAccessibleScenarios,
  findPrimaryScenarioByRouteName,
  findScenariosByRouteName,
  GUIDE_SCENARIOS,
  MODULES,
  REFERENCE_SCENARIOS,
  WORKFLOWS,
} from '@/guides/catalog'
import {getGuideStatus} from '@/guides/progress'
import {prepareRouteGuideAnchors} from '@/guides/anchors'

const {filterWorkflowNodes, firstAvailableTarget} = require('@/guides/runtimeCore.cjs')

export default {
  name: 'GuideCenter',
  props: {
    value: {type: Boolean, default: false},
    routeName: {type: String, default: ''},
    accessiblePaths: {type: Array, default: () => []},
  },
  data() {
    return {activeTab: 'page', referenceScenarios: REFERENCE_SCENARIOS}
  },
  computed: {
    currentPrimary() {
      return findPrimaryScenarioByRouteName(this.routeName)
    },
    currentScenarios() {
      return findScenariosByRouteName(this.routeName)
    },
    accessibleScenarios() {
      return filterAccessibleScenarios(GUIDE_SCENARIOS, this.accessiblePaths, this.routeName)
    },
    currentModuleScenarios() {
      if (!this.currentPrimary) return []
      return this.accessibleScenarios.filter((item) => item.module === this.currentPrimary.module)
    },
    moduleDescription() {
      return this.currentPrimary ? MODULES[this.currentPrimary.module]?.description || '' : ''
    },
    accessibleWorkflows() {
      return WORKFLOWS
          .map((item) => ({...item, accessibleNodes: filterWorkflowNodes(item.nodes, this.accessiblePaths)}))
          .filter((item) => item.accessibleNodes.length)
    },
  },
  watch: {
    value(visible) {
      if (visible) {
        this.activeTab = 'page'
        this.$nextTick(this.prepareAnchors)
      }
    },
    routeName() {
      if (this.value) this.$nextTick(this.prepareAnchors)
    },
  },
  methods: {
    prepareAnchors() {
      if (typeof document !== 'undefined') prepareRouteGuideAnchors(this.routeName, document)
    },
    stepAvailable(step) {
      return typeof document !== 'undefined' && Boolean(firstAvailableTarget(document, step.targets))
    },
    joinText(items) {
      return (items || []).filter(Boolean).join('；') || '按当前页面状态判断'
    },
    getStatus(item) {
      return getGuideStatus(item)
    },
    statusTag(item) {
      const labels = {
        VIEWED: {label: '看过', type: 'info'},
        COMPLETED: {label: '已了解', type: 'success'},
        DISMISSED: {label: '稍后再看', type: 'info'},
      }
      return labels[this.getStatus(item)] || {label: '第一次看', type: 'warning'}
    },
    close(done) {
      this.$emit('input', false)
      if (typeof done === 'function') done()
    },
    replay(item) {
      this.$emit('input', false)
      this.$nextTick(() => this.$emit('start-guide', item))
    },
  },
}
</script>

<style scoped>
.guide-center__shell {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  background: #f6f8fb;
}

.guide-center__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 26px 18px;
  color: #fff;
  background: linear-gradient(135deg, #003f88, #0057b7 62%, #1684dc);
}

.guide-center__header span {
  font-size: 12px;
  letter-spacing: .08em;
  opacity: .78;
}

.guide-center__header h2 {
  margin: 7px 0 6px;
  font-size: 24px;
}

.guide-center__header p {
  margin: 0;
  line-height: 1.6;
  opacity: .88;
}

.guide-center__header :deep(.el-button) {
  border-color: rgba(255, 255, 255, .45);
  color: #fff;
  background: rgba(255, 255, 255, .12);
}

.guide-center__tabs {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
}

.guide-center__tabs :deep(.el-tabs__header) {
  flex: none;
  margin: 0;
  padding: 0 18px;
  background: #fff;
}

.guide-center__tabs :deep(.el-tabs__content) {
  min-height: 0;
  flex: 1;
  overflow: auto;
}

.guide-center__tabs :deep(.el-tab-pane) {
  min-height: 100%;
}

.guide-center__content {
  display: grid;
  gap: 16px;
  padding: 20px;
}

.guide-center__page-intro,
.guide-center__section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.guide-center__page-intro div,
.guide-center__section-title div {
  display: grid;
  gap: 3px;
}

.guide-center__page-intro span,
.guide-center__section-title p {
  margin: 0;
  color: #667085;
  font-size: 13px;
}

.guide-center__section-title h3 {
  margin: 0;
}

.guide-task-card,
.guide-workflow-card,
.guide-login-reference,
.guide-intent-list article {
  border: 1px solid #e3e9f1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(31, 64, 104, .05);
}

.guide-task-card {
  padding: 18px;
}

.guide-task-card__head,
.guide-task-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.guide-task-card h3 {
  margin: 0;
  color: #172033;
  font-size: 17px;
}

.guide-task-card__answer {
  margin: 10px 0 14px;
  color: #0057b7;
  font-weight: 600;
  line-height: 1.65;
}

.guide-task-card__context {
  display: grid;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f6f9fd;
}

.guide-task-card__context p {
  display: flex;
  gap: 8px;
  margin: 0;
  color: #4d5d70;
  font-size: 13px;
  line-height: 1.6;
}

.guide-task-card__context i {
  margin-top: 4px;
  color: #0057b7;
}

.guide-task-card__warning {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  color: #9a5b00;
  font-size: 13px;
  line-height: 1.6;
}

.guide-task-card__steps {
  margin-top: 12px;
  padding: 13px 14px;
  border: 1px solid #e7edf5;
  border-radius: 10px;
}

.guide-task-card__steps > strong {
  color: #24364b;
  font-size: 13px;
}

.guide-task-card__steps ol {
  display: grid;
  gap: 12px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.guide-task-card__steps li {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 9px;
}

.guide-task-card__steps li > span {
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  background: #0057b7;
  font-size: 12px;
  font-weight: 700;
}

.guide-task-card__step-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.guide-task-card__step-head b {
  color: #24364b;
  font-size: 13px;
}

.guide-task-card__step-head small {
  flex: none;
  font-style: normal;
}

.guide-task-card__step-head small.is-available {
  color: #16794b;
}

.guide-task-card__step-head small.is-unavailable,
.guide-task-card__steps em {
  color: #9a5b00;
}

.guide-task-card__steps p {
  margin: 4px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.6;
}

.guide-task-card__steps em {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  font-style: normal;
  line-height: 1.55;
}

.guide-task-card__warning i {
  margin-top: 4px;
}

.guide-task-card__footer {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #edf1f5;
}

.guide-task-card__footer > span {
  color: #7b8794;
  font-size: 12px;
}

.guide-task-card__footer :deep(.el-button--primary) {
  border-color: #0057b7;
  background: #0057b7;
}

.guide-intent-list,
.guide-workflow-list {
  display: grid;
  gap: 12px;
}

.guide-intent-list article {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
}

.guide-intent-list article.is-current {
  border-color: rgba(0, 87, 183, .35);
  box-shadow: inset 3px 0 #0057b7;
}

.guide-intent-list article div {
  display: grid;
  gap: 5px;
}

.guide-intent-list small {
  color: #7b8794;
}

.guide-intent-list strong {
  color: #172033;
}

.guide-intent-list p {
  margin: 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.55;
}

.guide-workflow-card {
  padding: 17px;
}

.guide-workflow-card h3,
.guide-login-reference h3 {
  margin: 0 0 8px;
  color: #172033;
}

.guide-workflow-card p {
  margin: 0 0 13px;
  color: #5d6b7c;
  line-height: 1.65;
}

.guide-workflow-card__nodes {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.guide-workflow-card__nodes span {
  flex: none;
  padding: 6px 9px;
  border: 1px solid rgba(0, 87, 183, .18);
  border-radius: 999px;
  color: #0057b7;
  background: #f3f8ff;
  font-size: 12px;
}

.guide-workflow-card__nodes i,
.guide-workflow-card > small {
  color: #8491a2;
}

.guide-login-reference {
  display: grid;
  gap: 10px;
  padding: 17px;
}

.guide-login-reference article {
  padding: 12px;
  border-radius: 10px;
  background: #f6f9fd;
}

.guide-login-reference article p {
  margin: 6px 0;
  color: #526174;
  line-height: 1.55;
}

.guide-login-reference article small {
  color: #9a5b00;
}

@media (max-width: 600px) {
  .guide-center__header {
    padding: 20px 18px 16px;
  }

  .guide-center__content {
    padding: 14px;
  }

  .guide-task-card__footer,
  .guide-intent-list article {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
